import type { AuditData } from '@eye-able/types';
import { addDays, startOfDay } from 'date-fns';
import { getClosestDate } from '@/helpers/functions';

type ScheduledAuditEntry = Pick<
  AuditData,
  'lastAudit' | 'auditInterval' | 'allowedDays'
>;

export interface NextAuditSchedule {
  nextAudit: Date | null;
  nextAuditForToday: Date | null;
}

interface NextAuditScheduleOptions {
  /** Treat missing weekday restrictions as an unrestricted schedule. */
  useDateWithoutAllowedDays?: boolean;
}

function getClosestAllowedDate(
  date: Date,
  allowedDays: string | null | undefined,
  useDateWithoutAllowedDays: boolean,
): Date | null {
  const candidate = allowedDays
    ? getClosestDate(date, allowedDays)
    : useDateWithoutAllowedDays
      ? new Date(date)
      : null;
  return candidate instanceof Date && !Number.isNaN(candidate.getTime())
    ? candidate
    : null;
}

/** Shared interval, overdue and weekday calculation for report scheduling. */
export function getNextAuditSchedule(
  auditEntry: ScheduledAuditEntry | null | undefined,
  now = new Date(),
  { useDateWithoutAllowedDays = false }: NextAuditScheduleOptions = {},
): NextAuditSchedule {
  if (!auditEntry?.lastAudit || !auditEntry.auditInterval) {
    return { nextAudit: null, nextAuditForToday: null };
  }

  const lastAudit = new Date(auditEntry.lastAudit);
  if (Number.isNaN(lastAudit.getTime())) {
    return { nextAudit: null, nextAuditForToday: null };
  }

  const intervalDate = addDays(lastAudit, auditEntry.auditInterval);
  const nextAuditForToday = getClosestAllowedDate(
    now,
    auditEntry.allowedDays,
    useDateWithoutAllowedDays,
  );
  const intervalIsDue = startOfDay(now) >= startOfDay(intervalDate);
  const nextAudit = intervalIsDue
    ? nextAuditForToday
    : getClosestAllowedDate(
        intervalDate,
        auditEntry.allowedDays,
        useDateWithoutAllowedDays,
      );

  return { nextAudit, nextAuditForToday };
}
